/**
 * This picker use in modal without popup
 */

import Button from "../button/Button";
import Month from "../month/Month";
import { useOrderInfoContext } from "@src/app/order-info-context";
import { orderFlowCloseReason, useOrderFlowContext } from "@src/app/order-flow-context";

export interface DatePickerModel {
  title: string;
  ctaText: string;
  daysofWeekAvailable?: number[];
  constraint?: "none" | TimeConstraint;
  collectingFromPostcodeText?: string;
  changePostcodeText?: string;
}

interface TimeConstraint {
  validWeekSpan: number;
  daysBuffer: number;
}

interface Props {
  data: DatePickerModel;
  extraClassname?: string;
}

const calculateMonthDiff = (lowerDate: Date, upperDate: Date) => {
  return upperDate.getMonth() - lowerDate.getMonth() + 12 * (upperDate.getFullYear() - lowerDate.getFullYear());
};

const calculateValidTimeRange = (daysBuffer: number, validWeekSpan: number, selectedWeekdays: number[]) => {
  const weekDays = [6, 0, 1, 2, 3, 4, 5];
  const today = new Date();
  const todayAt0 = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const utcOffset = todayAt0.getTimezoneOffset();
  const todayTimeAt0 = todayAt0.getTime() - utcOffset * 60 * 1000;
  const earliestValidTime = todayTimeAt0 + (daysBuffer + 1) * 24 * 60 * 60 * 1000;
  const earliestValidWeekday = weekDays[new Date(earliestValidTime).getDay()];
  const sortedSelectedWeekdays = [...selectedWeekdays].sort((a, b) => a - b);
  let finalEarliestValidTime = earliestValidTime;
  if (!sortedSelectedWeekdays.includes(earliestValidWeekday)) {
    for (let i = 0; i < 6; i++) {
      const finalEarliestValidWeekday = weekDays[new Date(finalEarliestValidTime).getDay()];
      if (sortedSelectedWeekdays.includes(finalEarliestValidWeekday)) {
        break;
      } else {
        finalEarliestValidTime += 24 * 60 * 60 * 1000;
      }
    }
  }
  const finalEarliestValidDate = new Date(finalEarliestValidTime);

  const maxWeekDay = Math.max(...sortedSelectedWeekdays);
  const finalLatterValidTime =
    finalEarliestValidTime + ((validWeekSpan - 1) * 7 + maxWeekDay - weekDays[finalEarliestValidDate.getDay()]) * 24 * 60 * 60 * 1000;
  return { earliestValidTime: finalEarliestValidTime, latterValidTime: finalLatterValidTime };
};

const DatePicker = ({ data, extraClassname }: Props) => {
  const today = new Date();
  const todayAt0Utc = new Date(today.getFullYear(), today.getMonth(), today.getDate(), -today.getTimezoneOffset() / 60);

  const { firstCollection, setSchedule, schedule } = useOrderInfoContext();
  const { handleContinue, handleClose } = useOrderFlowContext();

  const { constraint } = data;
  let months: number;
  let validTimeRange:
    | {
        earliestValidTime: number;
        latterValidTime: number;
      }
    | undefined;

  if (!constraint) {
    validTimeRange = calculateValidTimeRange(1, 3, schedule ?? [0, 1, 2, 3, 4, 5, 6]);
    months = calculateMonthDiff(todayAt0Utc, new Date(validTimeRange.latterValidTime)) + 1;
  } else if (constraint === "none") {
    months = 6;
    validTimeRange = undefined;
  } else {
    validTimeRange = calculateValidTimeRange(constraint.daysBuffer, constraint.validWeekSpan, schedule ?? [0, 1, 2, 3, 4, 5, 6]);
    months = calculateMonthDiff(todayAt0Utc, new Date(validTimeRange.latterValidTime)) + 1;
  }

  if (data.daysofWeekAvailable && data.daysofWeekAvailable.length > 0) {
    setSchedule(data.daysofWeekAvailable);
  }

  return (
    <div className={`fm-o-date-picker ${extraClassname ?? ""}`}>
      <h2 className="fm-o-date-picker__title">{data.title}</h2>
      <div className="fm-o-date-picker__content">
        <div className={`fm-o-date-picker__months ${firstCollection ? "fm-o-date-picker__months--has-selection" : ""}`} tabIndex={-1}>
          {[...Array(months)].map((_, i) => (
            <Month key={i} date={new Date(today.getFullYear(), today.getMonth() + i, 1).getTime()} validTimeRange={validTimeRange} />
          ))}
        </div>
        {!firstCollection && data.collectingFromPostcodeText && data.changePostcodeText && (
          <div className="fm-o-date-picker__postcode-region">
            <div className="fm-o-date-picker__postcode">{data.collectingFromPostcodeText}</div>
            <div className="fm-o-date-picker__change-postcode" onClick={() => handleClose(orderFlowCloseReason.changePostCodeOrLocation)}>
              {data.changePostcodeText}
            </div>
          </div>
        )}
      </div>
      {firstCollection && (
        <div className="fm-o-date-picker__actions">
          <Button text={data.ctaText} onClick={handleContinue} mainClass="fm-o-date-picker__continue-btn" styleModifier={["fm-a-button--blue"]} />
        </div>
      )}
    </div>
  );
};

export default DatePicker;
